import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDuration } from '@/features/tasks/tree';

interface Row {
  task_id: number;
  title: string;
  estimate_seconds: number;
  logged_seconds: number;
  ratio: number | null;
  flag: string;
}

export function EstimateVsActualTable() {
  const rows = useQuery<Row[]>({
    queryKey: ['reports', 'estimate-vs-actual'],
    queryFn: () => api<Row[]>('/api/reports/estimate-vs-actual'),
  });

  if (rows.isPending) {
    return <div className="mt-8 h-24 animate-pulse rounded-lg bg-surface-2" />;
  }

  if (!rows.data?.length) {
    return (
      <section className="mt-8">
        <h2 className="text-body font-semibold">Actual vs estimate</h2>
        <p className="mt-2 text-small text-text-secondary">No task time in the last 30 days.</p>
      </section>
    );
  }

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-body font-semibold">Actual vs estimate (30 days)</h2>
      <ul className="flex flex-col gap-2 sm:hidden">
        {rows.data.map((r) => (
          <li key={r.task_id} className="rounded-lg border border-border bg-surface-1 px-3 py-2.5">
            <p className="truncate text-small font-medium">{r.title}</p>
            <dl className="mt-2 grid grid-cols-3 gap-2 text-label">
              <div>
                <dt className="text-text-tertiary">Estimate</dt>
                <dd className="tabular">
                  {formatDuration(Math.round(r.estimate_seconds / 60)) ?? '-'}
                </dd>
              </div>
              <div>
                <dt className="text-text-tertiary">Logged</dt>
                <dd className="tabular">
                  {formatDuration(Math.round(r.logged_seconds / 60)) ?? '-'}
                </dd>
              </div>
              <div>
                <dt className="text-text-tertiary">Ratio</dt>
                <dd
                  className={cn(
                    'tabular',
                    r.flag === 'over' && 'text-warning',
                    r.flag === 'under' && 'text-success',
                  )}
                >
                  {r.ratio != null ? `${r.ratio}×` : '-'}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto rounded-lg border border-border sm:block">
        <table className="w-full text-small">
          <thead>
            <tr className="border-b border-border text-left text-label uppercase text-text-tertiary">
              <th className="px-3 py-2 font-medium">Task</th>
              <th className="px-3 py-2 text-right font-medium">Estimate</th>
              <th className="px-3 py-2 text-right font-medium">Logged</th>
              <th className="px-3 py-2 text-right font-medium">Ratio</th>
            </tr>
          </thead>
          <tbody>
            {rows.data.map((r) => (
              <tr key={r.task_id} className="border-b border-border last:border-0">
                <td className="px-3 py-2">{r.title}</td>
                <td className="tabular px-3 py-2 text-right">
                  {formatDuration(Math.round(r.estimate_seconds / 60)) ?? '-'}
                </td>
                <td className="tabular px-3 py-2 text-right">
                  {formatDuration(Math.round(r.logged_seconds / 60)) ?? '-'}
                </td>
                <td
                  className={cn(
                    'tabular px-3 py-2 text-right',
                    r.flag === 'over' && 'text-warning',
                    r.flag === 'under' && 'text-success',
                  )}
                >
                  {r.ratio != null ? `${r.ratio}×` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
